import React, { useState } from "react";
import { useProxies, useCreateProxy, useDeleteProxy, useUpdateProxy, useTestProxy, useBulkImportProxies } from "@/lib/queries";
import { toast } from "sonner";
import { Globe2, Plus, Trash2, Send, Power, AlertTriangle, ShieldCheck, MapPin, FileText, Upload } from "lucide-react";
import { formatRelative } from "@/lib/cr";

const SELECT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308]";
const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252] font-mono";
const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";

const EMPTY = {
  label: "",
  protocol: "http",
  host: "",
  port: "",
  username: "",
  password: "",
  region: "",
  notes: "",
  active: true,
};

const PROTO_COLORS = {
  http: "#3B82F6",
  https: "#22C55E",
  socks5: "#A855F7",
  socks4: "#EC4899",
};

export default function ProxiesPage() {
  const [form, setForm] = useState(EMPTY);
  const [bulkText, setBulkText] = useState("");
  const [bulkProtocol, setBulkProtocol] = useState("http");
  const [bulkRegion, setBulkRegion] = useState("");
  const [bulkPrefix, setBulkPrefix] = useState("Imported");
  const { data: proxies = [] } = useProxies();
  const create = useCreateProxy();
  const del = useDeleteProxy();
  const update = useUpdateProxy();
  const test = useTestProxy();
  const bulk = useBulkImportProxies();

  const submit = (e) => {
    e.preventDefault();
    if (!form.label || !form.host || !form.port) {
      toast.error("Label, host and port are required");
      return;
    }
    create.mutate(
      { ...form, port: Number(form.port), username: form.username || null, password: form.password || null },
      {
        onSuccess: () => {
          toast.success("Proxy saved (credentials encrypted)");
          setForm(EMPTY);
        },
        onError: (e) => toast.error("Failed to save", { description: e?.response?.data?.detail }),
      },
    );
  };

  const submitBulk = () => {
    if (!bulkText.trim()) {
      toast.error("Paste proxies first");
      return;
    }
    bulk.mutate(
      { text: bulkText, default_protocol: bulkProtocol, region: bulkRegion || null, label_prefix: bulkPrefix || "Imported", active: true },
      {
        onSuccess: (r) => {
          toast.success(`Imported ${r.created} proxies`, {
            description: r.skipped ? `${r.skipped} lines skipped` : undefined,
          });
          setBulkText("");
        },
        onError: (e) => toast.error("Bulk import failed", { description: e?.response?.data?.detail }),
      },
    );
  };

  return (
    <div className="p-6 max-w-7xl w-full mx-auto" data-testid="proxies-page">
      <header className="mb-4">
        <div className="flex items-center gap-3">
          <Globe2 className="w-7 h-7 text-[#A855F7]" />
          <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
            Proxy <span className="text-[#A855F7]">pool</span>
          </h1>
        </div>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Route scalp attempts through residential or datacenter proxies. Credentials encrypted at rest.
        </p>
        <div className="mt-3 flex items-start gap-2 text-[11px] text-[#A3A3A3] bg-[#0d0d0d] border border-[#262626] p-3 rounded-sm">
          <ShieldCheck className="w-4 h-4 text-[#A855F7] shrink-0 mt-0.5" />
          <div>
            <strong className="text-white">How it works:</strong> Active proxies are randomly assigned per purchase attempt (rotation). Inactive proxies are skipped. Test before arming a Scalp profile — a failed proxy = a failed buy.
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <form
          onSubmit={submit}
          className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm space-y-4"
          data-testid="proxy-form"
        >
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#A855F7]" /> Add proxy
          </h2>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={LABEL}>Label</div>
              <input
                data-testid="proxy-label-input"
                className={INPUT}
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Sydney residential #1"
              />
            </div>
            <div>
              <div className={LABEL}>Protocol</div>
              <select
                data-testid="proxy-protocol-select"
                className={SELECT}
                value={form.protocol}
                onChange={(e) => setForm({ ...form, protocol: e.target.value })}
              >
                <option value="http">HTTP</option>
                <option value="https">HTTPS</option>
                <option value="socks5">SOCKS5</option>
                <option value="socks4">SOCKS4</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <div className={LABEL}>Host / IP</div>
              <input
                data-testid="proxy-host-input"
                className={INPUT}
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder="203.0.113.42"
              />
            </div>
            <div>
              <div className={LABEL}>Port</div>
              <input
                data-testid="proxy-port-input"
                type="number"
                className={INPUT}
                value={form.port}
                onChange={(e) => setForm({ ...form, port: e.target.value })}
                placeholder="8080"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={LABEL}>Username (optional)</div>
              <input
                data-testid="proxy-username-input"
                className={INPUT}
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="proxyuser"
              />
            </div>
            <div>
              <div className={LABEL}>Password (optional)</div>
              <input
                data-testid="proxy-password-input"
                type="password"
                className={INPUT}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={LABEL}>Region (optional)</div>
              <input
                data-testid="proxy-region-input"
                className={INPUT}
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                placeholder="AU, US, EU..."
              />
            </div>
            <div>
              <div className={LABEL}>Notes</div>
              <input
                data-testid="proxy-notes-input"
                className={INPUT}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="From provider X"
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="proxy-submit-btn"
            disabled={create.isPending}
            className="w-full bg-[#A855F7] text-white font-bold uppercase tracking-wider text-xs py-2.5 rounded-sm hover:bg-[#C084FC] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <ShieldCheck className="w-4 h-4" /> Encrypt &amp; save
          </button>

          {/* Bulk import */}
          <div className="border-t border-[#262626] mt-2 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-[#EAB308]" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider">Bulk import</h3>
            </div>
            <p className="text-[11px] text-[#737373] mb-3 leading-relaxed">
              Paste from Bright Data / Smartproxy / Webshare. One per line. Supported formats:
              <br />
              <code className="text-[#A3A3A3]">host:port</code> · <code className="text-[#A3A3A3]">host:port:user:pass</code> · <code className="text-[#A3A3A3]">user:pass@host:port</code> · <code className="text-[#A3A3A3]">protocol://user:pass@host:port</code>
            </p>
            <textarea
              data-testid="bulk-import-textarea"
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={"203.0.113.1:8080\n203.0.113.2:8080:user1:pass1\nuser2:pass2@203.0.113.3:8080"}
              rows={6}
              className={`${INPUT} font-mono text-xs resize-y`}
            />
            <div className="grid grid-cols-3 gap-2 mt-2">
              <select
                data-testid="bulk-protocol-select"
                value={bulkProtocol}
                onChange={(e) => setBulkProtocol(e.target.value)}
                className={`${SELECT} text-xs`}
              >
                <option value="http">HTTP</option>
                <option value="https">HTTPS</option>
                <option value="socks5">SOCKS5</option>
                <option value="socks4">SOCKS4</option>
              </select>
              <input
                data-testid="bulk-region-input"
                value={bulkRegion}
                onChange={(e) => setBulkRegion(e.target.value)}
                placeholder="Region (AU, US…)"
                className={`${INPUT} text-xs`}
              />
              <input
                data-testid="bulk-prefix-input"
                value={bulkPrefix}
                onChange={(e) => setBulkPrefix(e.target.value)}
                placeholder="Label prefix"
                className={`${INPUT} text-xs`}
              />
            </div>
            <button
              type="button"
              data-testid="bulk-import-btn"
              onClick={submitBulk}
              disabled={bulk.isPending}
              className="w-full mt-3 border border-[#EAB308] text-[#EAB308] font-bold uppercase tracking-wider text-xs py-2.5 rounded-sm hover:bg-[#EAB308]/10 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Upload className="w-4 h-4" /> Import batch
            </button>
          </div>
        </form>

        <div className="space-y-3">
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Pool ({proxies.length})
          </h2>
          {proxies.length === 0 ? (
            <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#737373] rounded-sm text-sm">
              No proxies yet. Add at least one to rotate.
            </div>
          ) : (
            proxies.map((p) => {
              const protoColor = PROTO_COLORS[p.protocol] || "#A3A3A3";
              return (
                <div
                  key={p.id}
                  data-testid={`proxy-item-${p.id}`}
                  className={`border p-4 rounded-sm card-hover ${p.active ? "bg-[#0d0d0d] border-[#A855F7]/30" : "bg-[#0a0a0a] border-[#262626] opacity-70"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm uppercase"
                          style={{ color: protoColor, borderColor: `${protoColor}55`, backgroundColor: `${protoColor}10` }}
                        >
                          {p.protocol}
                        </span>
                        <div className="text-sm font-bold text-white truncate">{p.label}</div>
                        {p.region && (
                          <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-[#EAB308]/30 text-[#EAB308] rounded-sm flex items-center gap-1">
                            <MapPin className="w-2.5 h-2.5" /> {p.region}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-[#A3A3A3]">
                        {p.host}:{p.port}
                        {p.has_auth && <span className="ml-2 text-[#737373]">· auth</span>}
                      </div>
                      {p.notes && <div className="text-[10px] text-[#737373] mt-1">{p.notes}</div>}
                      <div className="mt-2 flex items-center gap-3 text-[10px] font-mono">
                        {p.last_test_at ? (
                          <>
                            <span className={p.last_test_status === "ok" ? "text-[#22C55E]" : "text-[#EF4444]"}>
                              {p.last_test_status === "ok" ? "OK" : "FAILED"}
                            </span>
                            {p.last_exit_ip && <span className="text-[#A3A3A3]">exit: {p.last_exit_ip}</span>}
                            {p.last_latency_ms != null && <span className="text-[#737373]">{p.last_latency_ms}ms</span>}
                            <span className="text-[#737373]">· {formatRelative(p.last_test_at)}</span>
                          </>
                        ) : (
                          <span className="text-[#525252]">Untested</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        data-testid={`proxy-toggle-${p.id}`}
                        onClick={() =>
                          update.mutate(
                            { id: p.id, body: { active: !p.active } },
                            { onSuccess: () => toast.success(p.active ? "Proxy paused" : "Proxy active") },
                          )
                        }
                        className={`p-2 rounded-sm transition-colors ${
                          p.active ? "text-[#A855F7] hover:bg-[#A855F7]/10" : "text-[#737373] hover:text-white hover:bg-[#1A1A1A]"
                        }`}
                        title={p.active ? "Pause" : "Activate"}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`proxy-test-${p.id}`}
                        onClick={() =>
                          test.mutate(p.id, {
                            onSuccess: (r) =>
                              r.ok
                                ? toast.success(`Proxy OK — exit ${r.exit_ip} · ${r.latency_ms}ms`)
                                : toast.error("Proxy test failed", { description: r.error }),
                          })
                        }
                        disabled={test.isPending}
                        className="p-2 text-[#737373] hover:text-[#EAB308] hover:bg-[#1A1A1A] rounded-sm transition-colors disabled:opacity-60"
                        title="Test (returns exit IP via api.ipify.org)"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`proxy-delete-${p.id}`}
                        onClick={() => del.mutate(p.id, { onSuccess: () => toast.success("Proxy removed") })}
                        className="p-2 text-[#737373] hover:text-[#EF4444] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
